import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { API } from '../App';
import './SubscriptionPage.css';

const PLANS = [
  {
    key: 'starter',
    price: '49€',
    credits: 10,
  },
  {
    key: 'pro',
    price: '99€',
    credits: 40,
    featured: true,
  },
  {
    key: 'clinic',
    price: '199€',
    credits: 100,
  },
];

export default function SubscriptionPage() {
  const { accounts } = useMsal();
  const { t } = useTranslation();
  const practitionerId = (accounts[0]?.localAccountId || '').split(':')[0];
  const email          = accounts[0]?.username || '';

  const [subscription, setSubscription] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [loadingPlan,  setLoadingPlan]  = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const success  = params.get('success') === 'true';
  const canceled = params.get('canceled') === 'true';

  useEffect(() => {
    if (!practitionerId) return;
    fetch(`${API}/api/stripe/subscription/${practitionerId}`)
      .then(r => r.json())
      .then(d => setSubscription(d))
      .catch(() => setSubscription(null))
      .finally(() => setLoading(false));
  }, [practitionerId]);

  async function handleSubscribe(plan) {
    setLoadingPlan(plan);
    try {
      const res = await fetch(`${API}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ practitioner_id: practitionerId, email, plan }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      setLoadingPlan(null);
    }
  }

  async function handlePortal() {
    setPortalLoading(true);
    try {
      const res = await fetch(`${API}/api/stripe/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ practitioner_id: practitionerId }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      setPortalLoading(false);
    }
  }

  const activePlan = subscription?.plan || null;
  // Solde consolidé : quota mensuel + crédits offerts (bonus_credits). Le
  // bonus n'est détaillé que s'il y en a -- sinon la mention n'apporte rien.
  const credits    = subscription?.credits_total ?? subscription?.credits_remaining ?? 0;
  const bonus      = subscription?.bonus_credits ?? 0;

  return (
    <div className="subscription-page">
      <header className="sp-header">
        <div>
          <p className="sp-eyebrow">{t('subscription.eyebrow', 'Abonnement')}</p>
          <h1 className="sp-title">{t('subscription.title', 'Gérer votre abonnement')}</h1>
        </div>
      </header>

      {success && (
        <div className="sp-banner sp-banner--success">
          {t('subscription.success', 'Abonnement activé avec succès !')}
        </div>
      )}
      {canceled && (
        <div className="sp-banner sp-banner--warning">
          {t('subscription.canceled', 'Paiement annulé.')}
        </div>
      )}

      {!loading && activePlan && (
        <div className="sp-current">
          <div className="sp-current-info">
            <span className="sp-current-label">{t('subscription.current', 'Plan actif')}</span>
            <span className="sp-current-plan">{activePlan.charAt(0).toUpperCase() + activePlan.slice(1)}</span>
            <span className="sp-current-credits">{credits} {t('subscription.credits_left', 'consultations restantes')}</span>
            {bonus > 0 && (
              <span className="sp-current-bonus">
                {t('subscription.credits_offered', 'dont {{count}} offertes', { count: bonus })}
              </span>
            )}
          </div>
          <button className="btn-secondary" onClick={handlePortal} disabled={portalLoading}>
            {portalLoading ? '...' : t('subscription.manage', 'Gérer / Annuler')}
          </button>
        </div>
      )}

      {loading && <div className="sp-loading">…</div>}

      {!loading && (
        <div className="sp-plans">
          {PLANS.map(plan => (
            <div key={plan.key} className={`sp-card${plan.featured ? ' sp-card--featured' : ''}${activePlan === plan.key ? ' sp-card--active' : ''}`}>
              {plan.featured && (
                <span className="sp-badge">{t('subscription.popular', 'Le plus populaire')}</span>
              )}
              <div className="sp-plan-name">{plan.key.charAt(0).toUpperCase() + plan.key.slice(1)}</div>
              <div className="sp-plan-price">{plan.price}<span>/mois</span></div>
              <div className="sp-plan-credits">{plan.credits} {t('subscription.consultations', 'consultations/mois')}</div>

              {activePlan === plan.key ? (
                <button className="btn-active" disabled>
                  {t('subscription.active', 'Plan actif')} ✓
                </button>
              ) : (
                <button
                  className={plan.featured ? 'btn-primary' : 'btn-secondary'}
                  onClick={() => handleSubscribe(plan.key)}
                  disabled={loadingPlan === plan.key}
                >
                  {loadingPlan === plan.key ? '...' : t('subscription.subscribe', 'Choisir ce plan')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
